import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must a UAE VAT invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A UAE VAT tax invoice must include: the words "Tax Invoice" clearly displayed; the supplier name, address, and TRN (Tax Registration Number); the invoice date and a unique sequential invoice number; the customer name and address (and TRN if VAT-registered); a description of goods or services; the unit price, quantity, and line item total; the VAT rate (5%); the VAT amount; and the total amount including VAT, in AED.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the VAT rate in the UAE?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The UAE VAT rate is 5%, introduced on 1 January 2018. This applies to most goods and services. Some categories are zero-rated (0%), including most exports outside the GCC, international transportation, certain healthcare and education services. VAT is administered by the Federal Tax Authority (FTA).',
      },
    },
    {
      '@type': 'Question',
      name: 'Who needs to register for VAT in the UAE?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Businesses with annual taxable supplies exceeding AED 375,000 must register for VAT. Voluntary registration is available for businesses with supplies above AED 187,500. Once registered, the FTA issues a Tax Registration Number (TRN) which must appear on all tax invoices.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create a UAE VAT invoice online for free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Use the Invilic VAT invoice template. Enter your TRN in the VAT number field, set the VAT rate to 5%, and add your line items in AED. The VAT amount is calculated automatically. Download a professional PDF tax invoice instantly. Free for up to 5 invoices per month.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the difference between a simplified and full UAE VAT invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A full tax invoice is required for B2B transactions where the customer is VAT-registered, or for any invoice over AED 10,000. It must include all required fields including the customer TRN. A simplified tax invoice is allowed for retail transactions under AED 10,000 where the customer is not VAT-registered — it requires fewer details but must still show the VAT amount and total.',
      },
    },
  ],
};

export default function InvoiceGeneratorUAEPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator UAE: Free VAT Tax Invoice Maker | Invilic"
        description="Free VAT invoice generator for UAE businesses. Create FTA-compliant 5% VAT tax invoices with TRN. Download professional PDF instantly. No sign-up needed."
        canonical="https://www.invilic.com/invoice-generator-uae"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇦🇪 United Arab Emirates
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator UAE
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free VAT invoice generator for UAE businesses and freelancers. Create FTA-compliant tax invoices with your TRN and 5% VAT auto-calculated. Download a professional PDF in seconds — no account needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/create/vat"
              className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200"
            >
              Create VAT Tax Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/create/freelancer"
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-200"
            >
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* UAE VAT at a glance */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">UAE VAT at a Glance</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { label: 'VAT Rate', value: '5%', detail: 'Standard rate on most goods and services' },
              { label: 'Registration Threshold', value: 'AED 375,000', detail: 'Mandatory registration when annual taxable supplies exceed this' },
              { label: 'Voluntary Registration', value: 'AED 187,500', detail: 'Optional registration available above this threshold' },
            ].map(({ label, value, detail }) => (
              <div key={label} className="text-center p-5 rounded-xl border border-emerald-100 dark:border-emerald-800/30 bg-emerald-50 dark:bg-emerald-900/10">
                <div className="text-2xl font-extrabold text-emerald-700 dark:text-emerald-300 mb-1">{value}</div>
                <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{label}</div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Invoice types */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Which Invoice Do You Need?
          </h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              {
                icon: Percent,
                title: 'VAT Tax Invoice',
                subtitle: 'For VAT-registered businesses',
                features: ['TRN (Tax Registration Number)', '5% VAT auto-calculated', 'Net + VAT + total breakdown', 'AED billing', 'FTA-compliant format'],
                to: '/create/vat',
                color: 'emerald',
                btnClass: 'bg-emerald-600 hover:bg-emerald-700',
              },
              {
                icon: FileText,
                title: 'Standard Invoice',
                subtitle: 'For non-VAT-registered businesses',
                features: ['No VAT required', 'AED or USD billing', 'Custom line items', 'Logo upload', 'Payment terms'],
                to: '/create/freelancer',
                color: 'blue',
                btnClass: 'bg-blue-600 hover:bg-blue-700',
              },
              {
                icon: Globe,
                title: 'International Invoice',
                subtitle: 'For UAE businesses billing overseas',
                features: ['USD, EUR billing', 'SWIFT / IBAN fields', 'Multi-currency support', 'Zero-rated export format'],
                to: '/create/international',
                color: 'indigo',
                btnClass: 'bg-indigo-600 hover:bg-indigo-700',
              },
            ].map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={to}
                  className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}
                >
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* How to use for UAE VAT */}
        <section className="mb-16 p-8 rounded-2xl border border-emerald-100 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">
            How to Create a UAE VAT Invoice with Invilic
          </h2>
          <div className="space-y-3">
            {[
              'Click "Create VAT Tax Invoice" and select the VAT invoice type.',
              'Enter your TRN (Tax Registration Number) in the VAT Number field.',
              'Set the VAT rate to 5% — the VAT amount calculates automatically.',
              'Add your line items in AED with descriptions and amounts.',
              'Download the PDF. It includes net amount, 5% VAT, and gross total.',
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">{step}</span>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            UAE Invoice Generator: Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your UAE VAT Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">FTA-compliant PDF tax invoice in under 2 minutes. No account required.</p>
          <Link
            to="/create/vat"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200 text-lg"
          >
            Create VAT Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
